import { Component, Input, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { LicenseService } from '../../../../../services/remote/license.service';
import { NotificationService } from '../../../../../services/shared/notification.service';
import { ValidationMessageComponent } from '../../validation-message/validation-message.component';

@Component({
  selector: 'cognos-license-details-storage',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CheckboxModule,
    FloatLabelModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    DividerModule,
    TooltipModule,
    ValidationMessageComponent,
  ],
  templateUrl: './license-details-storage.component.html',
  styleUrl: './license-details-storage.component.scss',
})
export class LicenseDetailsStorageComponent {
  @Input() storageSettings: any;
  @Input({ required: true }) licenseId!: string;

  public formGroup!: FormGroup;
  public isLoading = false;

  private static fileSizeValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return { required: true };
    }
    if (value < 1) {
      return { min: { min: 1, actual: value } };
    }
    if (value > 1000) {
      return { max: { max: 1000, actual: value } };
    }
    return null;
  }

  constructor(
    private _licenseService: LicenseService,
    private _notificationService: NotificationService,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({
      maxFileSizeMB: [5, [LicenseDetailsStorageComponent.fileSizeValidator]],
      FAQs: [''],
      showFAQs: [true],
      safeguard: [''],
      showThumbs: [true],
      showContext: [true],
      showSources: [true],
      welcomeMessage: [''],
      showWelcomeMessage: [true],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storageSettings'] && changes['storageSettings'].currentValue) {
      const content =
        this.storageSettings?.content || this.storageSettings || {};

      this.formGroup.patchValue({
        maxFileSizeMB: content.maxFileSizeMB ?? 5,
      });
    }
  }

  public isSaveDisabled(): boolean {
    const maxFileSizeControl = this.formGroup.get('maxFileSizeMB');
    return (
      this.isLoading ||
      this.formGroup.invalid ||
      !maxFileSizeControl?.value ||
      maxFileSizeControl?.value < 1 ||
      maxFileSizeControl?.value > 1000
    );
  }

  public onSaveClick(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsDirty();
      });

      // Show specific validation errors
      const errors = this.getFormErrors();
      if (errors.length > 0) {
        this._notificationService.showErrorNotification(
          `Please fix the following errors: ${errors.join(', ')}`
        );
      } else {
        this._notificationService.showErrorNotification(
          'Please check the form fields.'
        );
      }
      return;
    }

    this._updateSettings(this.formGroup);
  }

  private getFormErrors(): string[] {
    const errors: string[] = [];

    Object.keys(this.formGroup.controls).forEach((key) => {
      const control = this.formGroup.get(key);
      if (control?.errors) {
        Object.keys(control.errors).forEach((errorKey) => {
          const errorValue = control.errors![errorKey];
          switch (errorKey) {
            case 'required':
              errors.push(`${key} is required`);
              break;
            case 'min':
              if (
                errorValue &&
                typeof errorValue === 'object' &&
                'min' in errorValue
              ) {
                errors.push(`${key} must be at least ${errorValue.min}`);
              } else {
                errors.push(`${key} must be at least the minimum value`);
              }
              break;
            case 'max':
              if (
                errorValue &&
                typeof errorValue === 'object' &&
                'max' in errorValue
              ) {
                errors.push(`${key} must be at most ${errorValue.max}`);
              } else {
                errors.push(`${key} must be at most the maximum value`);
              }
              break;
            case 'pattern':
              errors.push(`${key} has an invalid format`);
              break;
            default:
              errors.push(`${key} is invalid`);
          }
        });
      }
    });

    return errors;
  }

  private async _updateSettings(formGroup: FormGroup) {
    if (this.isLoading) return;

    this.isLoading = true;

    try {
      const typeValue = 'storage';

      // Get the current storage settings content
      const currentContent =
        this.storageSettings?.content || this.storageSettings || {};

      // Merge the form data with the existing storage settings
      const updatedContent = {
        ...currentContent,
        maxFileSizeMB: formGroup.get('maxFileSizeMB')?.value,
      };

      const updateResult = await this._licenseService.updateSettings(
        updatedContent,
        typeValue,
        this.licenseId
      );

      if (updateResult) {
        this._notificationService.showSuccessNotification(
          'Assistant settings updated successfully!'
        );
      }
    } catch (error: any) {
      console.error('Error updating file settings:', error);
      this._notificationService.showErrorNotification(
        error?.error?.detail || 'Failed to update assistant settings'
      );
    } finally {
      this.isLoading = false;
    }
  }
}
